import PropTypes from "prop-types";
import React from "react";
import { Alert, Button, Modal } from "@yoast/ui-library";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { provisionedSubscriptionShape } from "./provisionedSubscriptionShape";
import * as styles from "./ProvisioningStyles.module.scss";

const messages = defineMessages( {
	title: {
		id: "provisioning.refund.title",
		defaultMessage: "Mark subscription {number} as refunded",
	},
	ariaLabel: {
		id: "provisioning.refund.ariaLabel",
		defaultMessage: "Mark a provisioned subscription as refunded",
	},
	body: {
		id: "provisioning.refund.body",
		defaultMessage: "You are about to refund the subscription for {email}. It ends immediately.",
	},
	irreversible: {
		id: "provisioning.refund.irreversible",
		defaultMessage: "This action cannot be undone.",
	},
	cancel: {
		id: "provisioning.refund.cancel",
		defaultMessage: "Cancel",
	},
	confirm: {
		id: "provisioning.refund.confirm",
		defaultMessage: "Mark as refunded",
	},
	error: {
		id: "provisioning.refund.error",
		defaultMessage: "Could not refund the subscription. Please try again.",
	},
} );

/**
 * Confirms refunding a provisioned subscription.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The modal.
 */
function RefundProvisionedSubscriptionModal(
	{
		subscription = null,
		isSubmitting = false,
		hasError = false,
		errorMessage = null,
		onCancel,
		onConfirm,
		intl,
	},
) {
	return (
		<Modal
			isOpen={ Boolean( subscription ) }
			onClose={ onCancel }
			role="alertdialog"
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel>
				<Modal.Title as="h2" size="2">
					<FormattedMessage
						{ ...messages.title }
						values={ { number: subscription ? subscription.subscriptionNumber : "" } }
					/>
				</Modal.Title>
				<div className={ styles.modalBody }>
					<p>
						<FormattedMessage
							{ ...messages.body }
							values={ { email: <strong>{ subscription ? subscription.customerEmail : "" }</strong> } }
						/>
					</p>
					<p className={ styles.irreversible }>
						<FormattedMessage { ...messages.irreversible } />
					</p>
					{ hasError && (
						<Alert variant="error">
							{ errorMessage || <FormattedMessage { ...messages.error } /> }
						</Alert>
					) }
				</div>
				<div className={ styles.modalActions }>
					<Button variant="secondary" onClick={ onCancel } disabled={ isSubmitting }>
						<FormattedMessage { ...messages.cancel } />
					</Button>
					<Button
						variant="error"
						onClick={ onConfirm }
						isLoading={ isSubmitting }
						disabled={ isSubmitting }
					>
						<FormattedMessage { ...messages.confirm } />
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
}

RefundProvisionedSubscriptionModal.propTypes = {
	subscription: provisionedSubscriptionShape,
	isSubmitting: PropTypes.bool,
	hasError: PropTypes.bool,
	errorMessage: PropTypes.string,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( RefundProvisionedSubscriptionModal );
